import { computed, Injectable, Signal, signal, inject } from '@angular/core';
import { ClaimKey } from '../../models/api/core/claim-key';
import { ClaimViewResponse } from '../../models/api/view/claim-view/claim-view-response';
import { WatchlistClaim } from '../../models/api/view/watchlist/watchlist-claim';
import { Api } from '../api/api.service';
import { BaseService } from '../base.service';
import { Session } from '../session/session.service';
import { ClaimViewColumnService } from './claim-view-column.service';

@Injectable({
  providedIn: 'root',
})
export class WatchlistService extends BaseService {
  private api = inject(Api);
  private session = inject(Session);
  private claimViewColumnService = inject(ClaimViewColumnService);

  claims = signal<ClaimViewResponse | null>(null);
  private watchlistClaims = signal<WatchlistClaim[]>([]);

  constructor() {
    super();
    this.dispose.subs = this.session.cacheCleared.subscribe(() => {
      this.claims.set(null);
    });
    this.dispose.subs = this.claimViewColumnService.columnConfigRefreshed$.subscribe(
      () => this.forceLoad(),
    );
  }

  isWatching(source: string, id: string): Signal<boolean> {
    return computed(() =>
      this.watchlistClaims().some(
        (item) => item.recordId === id && item.recordSource === source,
      ),
    );
  }

  async load(): Promise<void> {
    if (this.claims() === null) {
      await this.forceLoad();
    }
  }

  /** Force reload so table refreshes with new column config and data (e.g. after saving columns). */
  async forceLoad(): Promise<void> {
    const [watchlist, columns] = await Promise.all([
      this.api.watchlist.get(true),
      this.claimViewColumnService.load(),
    ]);
    watchlist.columns = columns.columns;
    watchlist.unselectedColumns = columns.unselectedColumns;
    this.claims.set(watchlist);
    this.watchlistClaims.set(
      watchlist.rows.map((r: { recordId: string; recordSource: string }) => ({
        recordId: r.recordId,
        recordSource: r.recordSource,
      })),
    );
  }

  async add(claimKey: ClaimKey): Promise<void> {
    const current = this.claims();
    if (current) {
      const alreadyExists = current.rows.some(
        (r) =>
          r.recordId === claimKey.recordId &&
          r.recordSource === claimKey.recordSource,
      );
      if (!alreadyExists) {
        const updated = { ...current, rows: [...current.rows] };
        this.claims.set(updated);
        this.watchlistClaims.update((list) => [
          ...list,
          { recordId: claimKey.recordId, recordSource: claimKey.recordSource },
        ]);
      }
    }

    await this.api.watchlist.add(claimKey);

    // setting claims to null to force reload
    this.claims.set(null);
    await this.load();
  }

  async remove(claimKeys: ClaimKey[]): Promise<void> {
    const current = this.claims();
    if (current) {
      const updatedRows = current.rows.filter(
        (r) =>
          !claimKeys.some(
            (key) =>
              key.recordId === r.recordId &&
              key.recordSource === r.recordSource,
          ),
      );
      this.claims.set({ ...current, rows: updatedRows });
      this.watchlistClaims.update((list) =>
        list.filter(
          (item) =>
            !claimKeys.some(
              (key) =>
                key.recordId === item.recordId &&
                key.recordSource === item.recordSource,
            ),
        ),
      );
    }

    this.api.watchlist.remove(claimKeys);
  }

  async clear(): Promise<void> {
    this.claims.set({ ...this.claims(), rows: [] });
    this.watchlistClaims.set([]);
    this.api.watchlist.clear();
  }
}
